import { v4 } from 'uuid';
import { inspectionDB } from '../data/database.structure';
import {
  InspectionCategoryTypeModel,
  ObservationModel,
  ObservableItemModel,
} from '../data/types';
import {
  getEnumKeyByValue,
  ObservableLevelEnumValue,
  ObservableLevelsEnum,
} from '../data/enums';
import { cacheInvalidationService } from './cacheInvalidationService';
import { inspectionRecordService } from './inspection/inspectionRecordService';
import ObservationHelpers from '../helpers/observationHelpers';
import { toast } from '../store/toast';
import {
  EToastType,
  TOAST_DURATIONS,
} from '../components/molecules/ToastMessage';

/** Consecutive flush failures for one inspection before the user is warned. */
const FLUSH_FAILURE_TOAST_THRESHOLD = 3;

export interface ObservationDraftKeyProps {
  inspectionId: string;
  inspectionCategoryId: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  path: string;
  unitId?: number;
  unitName?: string;
}

export interface ObservationDraftSnapshot {
  /** Current draft value; null means "delete on flush" (tombstone). */
  value: string | null;
  /** True once the value has been seeded from Dexie (or set by the user first). */
  seeded: boolean;
  /** True when this path is a collection observable — drafts refuse it, use the legacy write path. */
  passthrough: boolean;
  dirty: boolean;
}

interface DraftEntry extends ObservationDraftSnapshot {
  props: ObservationDraftKeyProps;
  /** Monotonic stamp of the last USER action on this entry (0 = not a user action).
   * Lets supersede() distinguish drafts made before a legacy write began (stale,
   * stompable) from ones typed while it was in flight (newer, must win). */
  touchedAt: number;
}

const normalisePath = (path: string) =>
  path.replace('ObservableItemIdentifier.', '');

/**
 * The observation form layer (Phase 2 of #10345).
 *
 * Fields write here instead of straight to Dexie: values update in memory instantly
 * (and survive remounts — the R1 data-loss class), and a debounced flush writes ALL
 * dirty observations of an inspection in ONE Dexie 'rw' transaction with ONE record
 * write and ONE cache invalidation, instead of a full-record rewrite per field.
 * Collection observables (photos, multi-reading gauges) are refused — they keep
 * their existing immediate-write pipelines.
 *
 * Flush triggers: debounce after the last change, scope unmount (route change),
 * pagehide, and flushAll() before a sync push or a token-refresh redirect. A failed
 * flush keeps entries dirty and retries on the next trigger.
 */
export class ObservationDraftService {
  private entries = new Map<string, DraftEntry>();
  private listeners = new Map<string, Set<() => void>>();
  private dirtyByInspection = new Map<string, Set<string>>();
  private flushTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private flushChains = new Map<string, Promise<boolean>>();
  private seedPromises = new Map<string, Promise<void>>();
  private consecutiveFlushFailures = new Map<string, number>();
  /** Monotonic clock of user actions; see DraftEntry.touchedAt. */
  private touchClock = 0;
  private observableItemCache = new Map<
    string,
    ObservableItemModel | undefined
  >();

  constructor(private debounceMs = 600) {}

  keyOf(props: ObservationDraftKeyProps): string {
    return [
      props.inspectionId,
      props.inspectionCategoryId,
      props.dataLevel,
      props.entityId,
      props.entityExternalId,
      normalisePath(props.path),
    ].join('|');
  }

  subscribe(key: string, listener: () => void): () => void {
    let set = this.listeners.get(key);
    if (!set) {
      set = new Set();
      this.listeners.set(key, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
    };
  }

  getSnapshot(key: string): ObservationDraftSnapshot | undefined {
    return this.entries.get(key);
  }

  /**
   * Load the persisted value for a field once. A dirty or already-seeded entry is
   * never overwritten — the draft wins over whatever is on disk.
   */
  ensureSeeded(props: ObservationDraftKeyProps): Promise<void> {
    const key = this.keyOf(props);
    const existing = this.entries.get(key);
    if (existing && (existing.seeded || existing.dirty)) {
      return Promise.resolve();
    }
    const inFlight = this.seedPromises.get(key);
    if (inFlight) {
      return inFlight;
    }
    const promise = this.seedFromDatabase(key, props).finally(() => {
      this.seedPromises.delete(key);
    });
    this.seedPromises.set(key, promise);
    return promise;
  }

  setValue(props: ObservationDraftKeyProps, value: string): void {
    // An empty value is a retraction, not data: persisting '' created junk
    // observation rows that synced to the server (the bug-hunt found stray
    // empty NumberOfShoes/TotalTonnage rows). Route it to the tombstone.
    if (value === '') {
      this.markDeleted(props);
      return;
    }
    const key = this.keyOf(props);
    const previous = this.entries.get(key);
    if (previous?.passthrough) {
      return;
    }
    this.entries.set(key, {
      props,
      value,
      seeded: true,
      passthrough: false,
      dirty: true,
      touchedAt: ++this.touchClock,
    });
    this.markDirty(props.inspectionId, key);
    this.notify(key);
    this.scheduleFlush(props.inspectionId);
  }

  /** The current user-action stamp; legacy writers capture it BEFORE their write so
   * supersede() can tell which drafts predate the write. */
  currentStamp(): number {
    return this.touchClock;
  }

  /**
   * A legacy writer (Done buttons, batched GET mutations) just wrote this field
   * DIRECTLY to the database. Any pending draft for the same field is now stale:
   * left dirty, its later flush would clobber the newer legacy write - a stale
   * tombstone even deletes it (pack-1 model hunt find). The entry is replaced with
   * the legacy value, clean, so remounting draft-mode fields read the new value.
   * Before this hook every legacy writer had to remember to clear drafts itself
   * (OperatingHoursScreen did; others did not).
   *
   * `sinceStamp` is the writer's currentStamp() captured BEFORE its write began: a
   * draft the user touched AFTER that moment is NEWER than the legacy write and is
   * left dirty to win on its next flush (async legacy write racing fresh typing -
   * pack-1 hunt find HUNT-6).
   */
  supersede(
    props: ObservationDraftKeyProps,
    value: string | null,
    sinceStamp?: number,
  ): void {
    const key = this.keyOf(props);
    const previous = this.entries.get(key);
    if (previous?.passthrough) {
      return;
    }
    if (!previous && value === null) {
      return; // nothing drafted and nothing persisted to mirror
    }
    if (
      previous &&
      previous.dirty &&
      sinceStamp !== undefined &&
      previous.touchedAt > sinceStamp
    ) {
      return; // the user acted after this legacy write began - their draft wins
    }
    this.entries.set(key, {
      props,
      value,
      seeded: true,
      passthrough: false,
      dirty: false,
      touchedAt: 0,
    });
    this.dirtyByInspection.get(props.inspectionId)?.delete(key);
    this.notify(key);
  }

  /** Tombstone: the matching persisted observations are removed on flush. */
  markDeleted(props: ObservationDraftKeyProps): void {
    const key = this.keyOf(props);
    const previous = this.entries.get(key);
    if (previous?.passthrough) {
      return;
    }
    this.entries.set(key, {
      props,
      value: null,
      seeded: true,
      passthrough: false,
      dirty: true,
      touchedAt: ++this.touchClock,
    });
    this.markDirty(props.inspectionId, key);
    this.notify(key);
    this.scheduleFlush(props.inspectionId);
  }

  hasPendingChanges(inspectionId?: string): boolean {
    if (inspectionId) {
      return (this.dirtyByInspection.get(inspectionId)?.size ?? 0) > 0;
    }
    for (const keys of this.dirtyByInspection.values()) {
      if (keys.size > 0) {
        return true;
      }
    }
    return false;
  }

  scheduleFlush(inspectionId: string): void {
    const existing = this.flushTimers.get(inspectionId);
    if (existing) {
      clearTimeout(existing);
    }
    this.flushTimers.set(
      inspectionId,
      setTimeout(() => {
        this.flushTimers.delete(inspectionId);
        void this.flush(inspectionId);
      }, this.debounceMs),
    );
  }

  /**
   * Write every dirty draft of an inspection in one transaction / one record write.
   * Returns false (keeping the entries dirty for retry) instead of throwing — the
   * interactive path must never surface an exception mid-keystroke.
   */
  flush(inspectionId: string): Promise<boolean> {
    const timer = this.flushTimers.get(inspectionId);
    if (timer) {
      clearTimeout(timer);
      this.flushTimers.delete(inspectionId);
    }
    // Serialize flushes per inspection so two triggers can't interleave.
    const chain = (
      this.flushChains.get(inspectionId) ?? Promise.resolve(true)
    ).then(() => this.flushNow(inspectionId));
    this.flushChains.set(inspectionId, chain);
    return chain;
  }

  /** Flush every inspection with pending changes. Throws if any flush failed — the sync push must not silently run on stale data. */
  async flushAll(): Promise<void> {
    const inspectionIds = [...this.dirtyByInspection.entries()]
      .filter(([, keys]) => keys.size > 0)
      .map(([id]) => id);
    const results = await Promise.all(
      inspectionIds.map((id) => this.flush(id)),
    );
    if (results.some((ok) => !ok)) {
      throw new Error(
        'observationDraftService.flushAll: one or more inspections failed to flush',
      );
    }
  }

  /** Drop all entries for an inspection (after its scope closed and flushed). Dirty entries are kept. */
  release(inspectionId: string): void {
    for (const [key, entry] of this.entries) {
      if (entry.props.inspectionId === inspectionId && !entry.dirty) {
        this.entries.delete(key);
      }
    }
  }

  /**
   * #10285: drop every draft entry for one inspection whose path matches, DIRTY ENTRIES
   * INCLUDED - the one thing `release()` deliberately will not do.
   *
   * A writer that deletes rows straight from the inspection record cannot settle up with
   * the draft layer using `supersede()` alone, because supersede is keyed to a row that
   * existed: a dirty draft whose flush never landed (offline, or any failed
   * `ObservationDraftScope` unmount flush, which skips `release()`) has no row on disk, so
   * the writer never sees it. `flushNow` inserts a missing observation for a dirty draft
   * and `flushAll()` runs before every sync push, so that draft would re-create the row
   * the writer had just removed.
   *
   * Discarding rather than superseding is also what callers of this actually want: the
   * next `ensureSeeded` re-reads from disk, so a field shows whatever the writer left
   * behind - nothing after a delete, the restored value after an undo - instead of a
   * mirrored guess. Note it throws away typed-but-unsaved values by design; only call it
   * for a family of paths the caller means to discard, and never without an inspection.
   */
  discardMatching(
    inspectionId: string,
    matches: (path: string) => boolean,
  ): number {
    let discarded = 0;
    for (const [key, entry] of this.entries) {
      if (
        entry.props.inspectionId !== inspectionId ||
        !matches(normalisePath(entry.props.path))
      ) {
        continue;
      }
      this.entries.delete(key);
      this.dirtyByInspection.get(inspectionId)?.delete(key);
      this.notify(key);
      discarded += 1;
    }
    return discarded;
  }

  private async seedFromDatabase(
    key: string,
    props: ObservationDraftKeyProps,
  ): Promise<void> {
    try {
      const observableItem = await this.getObservableItem(
        normalisePath(props.path),
      );
      if (observableItem?.isCollection) {
        this.entries.set(key, {
          props,
          value: null,
          seeded: true,
          passthrough: true,
          dirty: false,
          touchedAt: 0,
        });
        this.notify(key);
        return;
      }

      const inspection = await inspectionDB.inspections
        .where('externalId')
        .equals(props.inspectionId)
        .first();
      const category = inspection?.inspectionCategoryTypes.find(
        (ct: InspectionCategoryTypeModel) =>
          ct.externalId === props.inspectionCategoryId,
      );
      const observation = observableItem
        ? category?.observations.find(
            (o: ObservationModel) =>
              o.observableItemId === observableItem.id &&
              ObservationHelpers.isObservationForEntity({
                observation: o,
                dataLevel: props.dataLevel,
                entityId: props.entityId,
                entityExternalId: props.entityExternalId,
              }),
          )
        : undefined;

      const current = this.entries.get(key);
      if (current && (current.seeded || current.dirty)) {
        return; // the user typed while we were reading — the draft wins
      }
      this.entries.set(key, {
        props,
        value: observation?.value ?? '',
        seeded: true,
        passthrough: false,
        dirty: false,
        touchedAt: 0,
      });
      this.notify(key);
    } catch (error) {
      console.error('observationDraftService.seed failed:', error);
    }
  }

  private async getObservableItem(
    pathModified: string,
  ): Promise<ObservableItemModel | undefined> {
    if (this.observableItemCache.has(pathModified)) {
      return this.observableItemCache.get(pathModified);
    }
    const item = await inspectionDB.observableItems
      .where('identifier')
      .equals(pathModified)
      .first();
    // Never cache a miss: the vocabulary may simply not have synced yet, and a
    // cached undefined would keep the field unpersistable for the entire session
    // even after the reference data arrives (pack-1 model hunt find).
    if (item) {
      this.observableItemCache.set(pathModified, item);
    }
    return item;
  }

  private markDirty(inspectionId: string, key: string): void {
    let keys = this.dirtyByInspection.get(inspectionId);
    if (!keys) {
      keys = new Set();
      this.dirtyByInspection.set(inspectionId, keys);
    }
    keys.add(key);
  }

  private notify(key: string): void {
    this.listeners.get(key)?.forEach((listener) => listener());
  }

  private async flushNow(inspectionId: string): Promise<boolean> {
    const dirtyKeys = [...(this.dirtyByInspection.get(inspectionId) ?? [])];
    if (dirtyKeys.length === 0) {
      return true;
    }
    const changes = dirtyKeys
      .map((key) => this.entries.get(key))
      .filter(
        (entry): entry is DraftEntry =>
          !!entry && entry.dirty && !entry.passthrough,
      );
    if (changes.length === 0) {
      dirtyKeys.forEach((key) =>
        this.dirtyByInspection.get(inspectionId)?.delete(key),
      );
      return true;
    }

    try {
      // Resolve observable items outside the transaction (static reference data).
      const resolved: {
        entry: DraftEntry;
        observableItem: ObservableItemModel;
      }[] = [];
      let unresolvedCount = 0;
      for (const entry of changes) {
        const pathModified = normalisePath(entry.props.path);
        const observableItem = await this.getObservableItem(pathModified);
        if (!observableItem) {
          // Vocabulary not (yet) synced for this identifier. The entry can't persist,
          // so this flush MUST NOT report success: the sync push treats a resolved
          // flushAll as "everything typed is on disk" and would push while the value
          // silently lives in memory only, dying on reload (pack-1 model hunt find).
          console.error(
            'observationDraftService.flush: unknown observable item',
            pathModified,
          );
          unresolvedCount++;
          continue;
        }
        resolved.push({ entry, observableItem });
      }

      if (resolved.length === 0 && unresolvedCount > 0) {
        this.onFlushFailure(inspectionId);
        return false;
      }

      const enumObj = Object.fromEntries(
        Object.entries(ObservableLevelsEnum).map((e) => [e[0], e[1] as number]),
      );

      // What this flush ACTUALLY wrote per key (value or tombstone) - the basis for
      // marking entries clean afterwards.
      const writtenByKey = new Map<string, string | null>();

      const stored = await inspectionRecordService.change(
        inspectionId,
        (inspection) => {
          for (const { entry, observableItem } of resolved) {
            // Re-read the LIVE entry inside the transaction. The `resolved` list was
            // captured before the transaction opened, and a legacy write can land -
            // and supersede the draft - in that window: writing the captured value
            // would clobber the newer legacy write with stale data (a debounce flush
            // racing a Done-button write; pack-1 concurrency hunt find). An entry
            // that went clean mid-flight is skipped; one re-typed mid-flight flushes
            // its newest value.
            const liveKey = this.keyOf(entry.props);
            const live = this.entries.get(liveKey);
            if (!live || !live.dirty || live.passthrough) {
              continue;
            }
            const category = inspection.inspectionCategoryTypes.find(
              (ct: InspectionCategoryTypeModel) =>
                ct.externalId === live.props.inspectionCategoryId,
            );
            if (!category) {
              throw new Error(
                'observationDraftService.flush: inspection category not found',
              );
            }
            const matches = (o: ObservationModel) =>
              o.observableItemId === observableItem.id &&
              ObservationHelpers.isObservationForEntity({
                observation: o,
                dataLevel: live.props.dataLevel,
                entityId: live.props.entityId,
                entityExternalId: live.props.entityExternalId,
              });

            if (live.value === null) {
              // Tombstone: unit-AGNOSTIC deletion. Draft entries are never collections
              // (passthrough refuses them), and for non-collections the unit is
              // metadata, not identity - a unit-less legacy writer can (re)create the
              // row with unitId -1, and a unit-strict filter then never matches it
              // again, leaving an undeletable observation (pack-1 hunt, seed 99).
              category.observations = category.observations.filter(
                (o: ObservationModel) => !matches(o),
              );
              writtenByKey.set(liveKey, null);
              continue;
            }

            const existing = category.observations.find(matches);
            const dataLevelName = getEnumKeyByValue(
              enumObj,
              live.props.dataLevel,
            ) as string;
            if (existing) {
              existing.value = live.value;
              existing.unitId = live.props.unitId ?? -1;
              existing.unit = live.props.unitName ?? existing.unit ?? null;
              existing.isSynced = false;
            } else {
              category.observations.push({
                id: 0,
                externalId: v4(),
                value: live.value,
                entityId: live.props.entityId,
                entityExternalId: live.props.entityExternalId,
                dataLevelId: live.props.dataLevel,
                categoryTypeId: category.id,
                unitId: live.props.unitId ?? -1,
                observableItemId: observableItem.id,
                isSynced: false,
                actionTypeId: -1,
                dataLevel: dataLevelName,
                unit: live.props.unitName ?? null,
                actionType: '',
                observableItem,
                fileReceived: false,
              });
            }
            writtenByKey.set(liveKey, live.value);
          }
        },
      );
      if (!stored) {
        throw new Error('observationDraftService.flush: inspection not found');
      }

      for (const [key, writtenValue] of writtenByKey) {
        const current = this.entries.get(key);
        // Only mark clean if the entry still holds exactly what was written -
        // typing (or a supersede) that landed after the write keeps it dirty/clean
        // on its own terms.
        if (current && current.dirty && current.value === writtenValue) {
          this.entries.set(key, { ...current, dirty: false });
          this.dirtyByInspection.get(inspectionId)?.delete(key);
        }
      }

      cacheInvalidationService.invalidateInspectionAfterObservationChange(
        inspectionId,
      );
      if (unresolvedCount > 0) {
        // The resolvable entries above ARE persisted, but unresolved ones remain
        // dirty - report failure so flushAll throws and the sync push refuses to
        // run while typed data cannot reach the database.
        this.onFlushFailure(inspectionId);
        return false;
      }
      this.consecutiveFlushFailures.delete(inspectionId);
      return true;
    } catch (error) {
      console.error(
        'observationDraftService.flush failed (entries stay dirty for retry):',
        error,
      );
      this.onFlushFailure(inspectionId);
      return false;
    }
  }

  private onFlushFailure(inspectionId: string): void {
    const count = (this.consecutiveFlushFailures.get(inspectionId) ?? 0) + 1;
    this.consecutiveFlushFailures.set(inspectionId, count);
    // Warn at the threshold, then again every N further failures - loud enough that
    // the user notices unsynced changes, quiet enough not to spam every retry.
    if (
      count >= FLUSH_FAILURE_TOAST_THRESHOLD &&
      count % FLUSH_FAILURE_TOAST_THRESHOLD === 0
    ) {
      toast.add({
        message:
          "Some changes on this inspection haven't saved. Check your connection and try again.",
        type: EToastType.alert,
        isOpen: true,
        options: { duration: TOAST_DURATIONS.ERROR },
      });
    }
  }
}

export const observationDraftService = new ObservationDraftService();

// Best effort on tab close/backgrounding — the primary guarantees are the scope
// unmount flush and flushAll() before sync. 'visibilitychange -> hidden' fires
// BEFORE 'pagehide' and, unlike pagehide, is reliably delivered when Android
// backgrounds (and may then kill) the PWA process - pagehide alone leaves a gap
// where a debounced draft can be lost on an OS-initiated kill.
if (typeof window !== 'undefined') {
  const flushIfPending = () => {
    if (observationDraftService.hasPendingChanges()) {
      void observationDraftService.flushAll().catch(() => undefined);
    }
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushIfPending();
    }
  });
  window.addEventListener('pagehide', flushIfPending);
}
